import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, parseErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Mail, RefreshCw, ArrowRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const RECAP_ROLES = ['owner', 'operations'];

export function WeeklyRecapCard({ compact = false }) {
  const { user } = useAuth();
  const allowed = RECAP_ROLES.includes(user?.role);
  const [recap, setRecap] = useState(null);
  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/commitments/weekly_recap');
      setRecap(r.data);
    } catch (e) { if (!compact) toast.error(parseErr(e)); }
    finally { setLoading(false); }
  }, [compact]);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  useEffect(() => {
    if (!allowed) return;
    api.get('/master-projects/health/slip-reasons?weeks=1')
      .then(r => setSlip(r.data)).catch(() => {});
  }, [allowed]);

  if (!allowed) return null;
  if (!recap) {
    return loading ? (
      <div className="card p-4 text-[13px] italic text-[hsl(var(--foreground-muted))]" data-testid="weekly-recap-loading">
        Preparing weekly recap…
      </div>
    ) : null;
  }
  const s = recap.summary || {};
  return (
    <div className="card p-4" data-testid="weekly-recap-card">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="font-heading text-[15px] font-semibold flex items-center gap-2">
            <Mail size={15} />Weekly Ops Recap
            <span className="chip tabular-nums">week of {recap.week_of}</span>
          </div>
          <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5">
            A readable Monday summary for Owner &amp; Operations. Email dispatch can be switched on later.
          </div>
        </div>
        {!compact && (
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="weekly-recap-refresh">
            <RefreshCw size={13} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mt-3" data-testid="weekly-recap-chips">
        <span className="chip chip-info">{s.shift_events ?? 0} shifts</span>
        <span className={`chip ${s.shifts_pending ? 'chip-warning' : ''}`}>{s.shifts_pending ?? 0} pending</span>
        <span className={`chip ${s.overloads ? 'chip-danger' : ''}`}>{s.overloads ?? 0} overloads</span>
        <span className="chip chip-success">{s.completed_on_time ?? 0} on time</span>
        <span className={`chip ${s.completed_late ? 'chip-warning' : ''}`}>{s.completed_late ?? 0} late</span>
        <span className={`chip ${s.overdue_now ? 'chip-danger' : ''}`}>{s.overdue_now ?? 0} overdue now</span>
        {s.escalations > 0 && (
          <span className="chip chip-danger" data-testid="weekly-recap-escalations">
            <AlertTriangle size={11} className="inline mr-0.5" />{s.escalations} escalation{s.escalations === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {slip?.overall_top?.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11.5px]" data-testid="weekly-recap-slip-reasons">
          <span className="text-[hsl(var(--foreground-muted))] font-medium">Top slip causes this week:</span>
          {slip.overall_top.map(t => (
            <span key={t.reason} className="chip chip-warning">{t.label} ×{t.count}</span>
          ))}
        </div>
      )}
      {compact ? (
        <Link to="/commitments?tab=digest" className="text-[12.5px] text-[hsl(var(--primary))] underline flex items-center gap-1 mt-3 w-fit" data-testid="weekly-recap-open-link">
          Read the full recap <ArrowRight size={12} />
        </Link>
      ) : (
        <pre className="mt-3 p-3 rounded-md border text-[12.5px] leading-relaxed whitespace-pre-wrap font-mono overflow-x-auto"
             style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-1))' }}
             data-testid="weekly-recap-text">{recap.plain_text}</pre>
      )}
    </div>
  );
}
